/**
 * Cross-site "what needs a human right now", assembled client-side.
 *
 * There is no cross-site endpoint: `GET /ce/generation-jobs` requires `site_id`, and `POST /ce/search`
 * explicitly returns zero bundles unless a site is supplied. So this fans out one request per site,
 * the same pattern CESiteOverview and CEEvidenceReview already use. Site counts are single-digit.
 *
 * THE RULE THIS MODULE EXISTS TO ENFORCE: a site whose lookup fails is reported as UNCHECKED, never
 * dropped. `Promise.allSettled` makes it trivially easy to keep the fulfilled results and move on —
 * and on this particular screen that produces a page which says "everything is current" while a site
 * it could not reach has packets waiting on someone. Unknown is not the same as fine, and this is the
 * one screen where confusing them matters most.
 *
 * `GenerationJobResponse` carries no site_id, site_name or period, so site attribution is done here
 * from which fan-out request returned the row, and the deficiency detail needs one extra
 * `/review` call per parked job.
 */
import api from './api';
// The deficiency wording lives in services/ceDeficiency.js so Attention and the review flow say the
// same thing about the same numbers. It moved there when the rounding bug was found: this file's
// previous Math.round produced "missing 100% of control mapping ce005" for a category that had 5 of
// 1465 included — a false statement, live on the landing page. Re-exported so existing importers
// keep working.
import { summariseDeficiency } from './ceDeficiency';

export { summariseDeficiency };

/**
 * Per-request ceiling for the fan-out. Measured on dev 2026-08-14: four of five sites answer
 * /collectors in ~0.15s and one takes SIXTY seconds. Without a ceiling the landing page — the
 * first screen anyone opens — blocks for a minute on the slowest site.
 *
 * A site that exceeds this is reported as UNCHECKED, which is already this module's rule and is
 * the honest answer: we do not know its state. That is materially different from deciding it is
 * fine, which is the failure this whole screen is designed against.
 *
 * The slow endpoint itself is a backend concern and is deliberately not worked around here beyond
 * refusing to wait for it.
 */
const FANOUT_TIMEOUT_MS = 8000;

async function sitesList() {
  const { data } = await api.get('/ce/sites');
  return Array.isArray(data) ? data : (data && data.sites) || [];
}

/**
 * @returns {{items: Array, unchecked: Array, sites: Array}}
 *   items     — one per thing needing a human, worst first
 *   unchecked — sites whose lookup failed; MUST be surfaced, never silently omitted
 */
let _inflight = null;
let _cachedAt = 0;
const CACHE_MS = 15000;

/**
 * The sidebar badge and the Attention page both need this, and both mount together — without
 * sharing, every page load fires the fan-out TWICE (measured: 24 requests where 12 would do).
 * A short window is enough to collapse that without making the page feel stale.
 */
export function fetchAttentionShared() {
  const now = Date.now();
  if (!_inflight || now - _cachedAt > CACHE_MS) {
    _cachedAt = now;
    _inflight = fetchAttention().catch((err) => { _inflight = null; throw err; });
  }
  return _inflight;
}

export function __resetAttentionCache() { _inflight = null; _cachedAt = 0; }

export async function fetchAttention() {
  const sites = await sitesList();
  const unchecked = [];
  const items = [];

  // ── packets awaiting review ────────────────────────────────────────────────
  const jobResults = await Promise.allSettled(sites.map((s) =>
    api.get(`/ce/generation-jobs?site_id=${s.id}&state=AWAITING_REVIEW&limit=200`,
            { timeout: FANOUT_TIMEOUT_MS })));

  const parked = [];
  sites.forEach((site, i) => {
    const r = jobResults[i];
    if (r.status !== 'fulfilled' || !Array.isArray(r.value.data)) {
      unchecked.push({ site, reason: 'packets awaiting review' });
      return;
    }
    r.value.data.forEach((job) => parked.push({ site, job }));
  });

  // The deficiency detail is a second call per parked job — GenerationJobResponse does not carry it.
  // A failed /review does not hide the packet: it still needs a human, we just cannot yet say how badly.
  const reviews = await Promise.allSettled(
    parked.map(({ job }) =>
      api.get(`/ce/generation-jobs/${job.job_id}/review`, { timeout: FANOUT_TIMEOUT_MS })),
  );
  parked.forEach(({ site, job }, i) => {
    const r = reviews[i];
    const detail = r.status === 'fulfilled' ? r.value.data : null;
    items.push({
      kind: 'review',
      key: `review-${job.job_id}`,
      siteId: site.id,
      siteName: site.site_name,
      jobId: job.job_id,
      summary: detail ? summariseDeficiency(detail.completeness) : 'deficiency detail unavailable',
      severity: 0, // a human decision outranks a health warning
    });
  });

  // COLLECTOR HEALTH IS DELIBERATELY NOT HERE.
  //
  // It was built, shipped, and removed after seeing it on real data: every site on dev has a
  // collector reporting `unknown`, so Attention rendered five monitoring warnings around the one
  // row that actually needed a person. `unknown` is derived purely from last_seen_at recency, and
  // time_sync_status is uninstrumented fleet-wide, so that noise is close to permanent.
  //
  // The distinction that settles it: this screen is for things requiring a DECISION right now --
  // something to authorize, something to review. A collector's health is monitoring signal. There
  // is nothing to act on, and mixing it in drowns the actionable row every single time. Fleet-wide
  // collector health belongs somewhere like Sites & Setup, as its own concern.
  //
  // Note this also removes the 60-second /collectors call from the landing page's critical path.
  // The timeout ceiling above stays regardless -- it is correct on its own terms, and the jobs
  // fan-out can be slow too.

  items.sort((a, b) => a.severity - b.severity);
  return { items, unchecked, sites };
}

/**
 * Count for the nav badge. Deliberately returns null — not 0 — when the fan-out could not complete,
 * so a badge can render "?" rather than a confident zero it has not earned.
 */
export async function fetchAttentionCount() {
  try {
    const { items, unchecked } = await fetchAttentionShared();
    if (unchecked.length) return null;
    return items.length;
  } catch {
    return null;
  }
}
